/**
 * networkUtils.js — Shared utility functions for supply chain analytics views.
 */

export function formatEur(val) {
  if (val == null || val === 0) return '€0'
  if (val >= 1e6) return `€${(val / 1e6).toFixed(1)}M`
  if (val >= 1e3) return `€${(val / 1e3).toFixed(0)}K`
  return `€${val.toFixed(0)}`
}

export function formatEurFull(val) {
  if (val == null) return '€0'
  return `€${val.toLocaleString('de-DE', { maximumFractionDigits: 0 })}`
}

export function savingsRange(raw) {
  return { lo: raw * 0.5, hi: raw * 0.8 }
}

export function formatRange(raw) {
  const r = savingsRange(raw)
  return `${formatEur(r.lo)} – ${formatEur(r.hi)}`
}

/**
 * Compute price position for a supplier entry within a cohort.
 * Formula: benchmark = cohort_median_UC × (cohort_median_EAU / part_EAU)^0.4
 * price_position = actual_unit_cost / benchmark
 */
export function computePricePositions(parts) {
  // Group by sourcing_cohort
  const cohorts = {}
  parts.forEach(p => {
    const coh = p.sourcing_cohort || p.family
    if (!cohorts[coh]) cohorts[coh] = []
    p.suppliers.forEach(s => {
      if (s.unit_cost > 0) {
        cohorts[coh].push({
          unit_cost: s.unit_cost,
          eau: parseFloat(s.eau) || 0,
        })
      }
    })
  })

  // Compute cohort medians
  const cohortStats = {}
  Object.entries(cohorts).forEach(([coh, entries]) => {
    if (entries.length < 3) return
    const costs = entries.map(e => e.unit_cost).sort((a, b) => a - b)
    const eaus = entries.filter(e => e.eau > 0).map(e => e.eau).sort((a, b) => a - b)
    cohortStats[coh] = {
      median_uc: costs[Math.floor(costs.length / 2)],
      median_eau: eaus.length > 0 ? eaus[Math.floor(eaus.length / 2)] : 0,
    }
  })

  // Assign price positions
  return parts.map(p => {
    const coh = p.sourcing_cohort || p.family
    const stats = cohortStats[coh]
    if (!stats) return p

    const enrichedSuppliers = p.suppliers.map(s => {
      if (!s.unit_cost || s.unit_cost <= 0) return s
      const eau = parseFloat(s.eau) || 0
      if (eau <= 0 || stats.median_eau <= 0) return s

      const benchmark = stats.median_uc * Math.pow(stats.median_eau / eau, 0.4)
      const pp = s.unit_cost / benchmark

      return { ...s, price_position: Math.round(pp * 100) / 100, benchmark: Math.round(benchmark * 100) / 100 }
    })

    return { ...p, suppliers: enrichedSuppliers }
  })
}

/**
 * Detect savings opportunities from parts data.
 */
export function computeOpportunities(parts) {
  const opps = []

  // 1. Negotiation targets: PP > 1.3, significant spend
  const negoTargets = []
  parts.forEach(p => {
    p.suppliers.forEach(s => {
      if (s.price_position && s.price_position > 1.3 && s.spend > 500) {
        const overspend = s.spend * (1 - 1 / s.price_position)
        negoTargets.push({ part: p, supplier: s, overspend })
      }
    })
  })

  // Group by supplier
  const negoBySupplier = {}
  negoTargets.forEach(t => {
    const key = t.supplier.name
    if (!negoBySupplier[key]) negoBySupplier[key] = { supplier: key, items: [], totalOverspend: 0, totalSpend: 0 }
    negoBySupplier[key].items.push(t)
    negoBySupplier[key].totalOverspend += t.overspend
    negoBySupplier[key].totalSpend += t.supplier.spend
  })

  Object.values(negoBySupplier).sort((a, b) => b.totalOverspend - a.totalOverspend).forEach(g => {
    if (g.totalOverspend < 500) return
    const uniqueParts = [...new Map(g.items.map(i => [i.part.pn, i.part])).values()]
    const avgPP = g.items.reduce((s, i) => s + i.supplier.price_position, 0) / g.items.length
    opps.push({
      type: 'negotiation',
      title: `${g.supplier} — ${g.items.length} parts above expected price`,
      parts: uniqueParts,
      spendExposed: g.totalSpend,
      estSavings: g.totalOverspend,
      confidence: g.items.length >= 5 ? 'High' : g.items.length >= 2 ? 'Medium' : 'Low',
      avgPP,
      detail: `Average price position ${avgPP.toFixed(2)}x. ${g.items.length} line items priced ${Math.round((avgPP - 1) * 100)}% above cohort benchmark.`,
    })
  })

  // 2. Sole-source risk
  const soleParts = parts.filter(p => p.suppliers.length === 1 && p.total_spend > 2000)
  const soleBySupplier = {}
  soleParts.forEach(p => {
    const key = p.suppliers[0].name
    if (!soleBySupplier[key]) soleBySupplier[key] = { supplier: key, parts: [], totalSpend: 0 }
    soleBySupplier[key].parts.push(p)
    soleBySupplier[key].totalSpend += p.total_spend
  })

  Object.values(soleBySupplier).sort((a, b) => b.totalSpend - a.totalSpend).forEach(g => {
    if (g.totalSpend < 5000) return
    opps.push({
      type: 'sole_source',
      title: `${g.supplier.substring(0, 35)} — ${g.parts.length} sole-sourced parts`,
      parts: g.parts,
      spendExposed: g.totalSpend,
      estSavings: g.totalSpend * 0.12,
      confidence: g.parts.length >= 5 ? 'High' : 'Medium',
      detail: `${formatEur(g.totalSpend)} spend at risk with no alternative supplier.`,
    })
  })

  // 3. Consolidation: families with 4+ suppliers
  const familyMap = {}
  parts.forEach(p => {
    const fam = p.family || 'unassigned'
    if (!familyMap[fam]) familyMap[fam] = { parts: [], suppliers: new Set(), totalSpend: 0 }
    familyMap[fam].parts.push(p)
    familyMap[fam].totalSpend += p.total_spend
    p.suppliers.forEach(s => familyMap[fam].suppliers.add(s.name))
  })

  Object.entries(familyMap).sort((a, b) => b[1].totalSpend - a[1].totalSpend).forEach(([fam, data]) => {
    if (data.suppliers.size < 4 || data.totalSpend < 10000) return
    opps.push({
      type: 'consolidation',
      title: `${fam.replace(/_/g, ' ')} — ${data.suppliers.size} suppliers could consolidate`,
      parts: data.parts,
      spendExposed: data.totalSpend,
      estSavings: data.totalSpend * 0.08,
      confidence: data.parts.length >= 8 ? 'High' : 'Medium',
      detail: `Family spread across ${data.suppliers.size} suppliers (${data.parts.length} parts, ${formatEur(data.totalSpend)} spend).`,
    })
  })

  // 4. Quick wins
  parts.forEach(p => {
    if (p.suppliers.length < 2) return
    const expensive = p.suppliers.filter(s => s.price_position && s.price_position > 1.5)
    const cheaper = p.suppliers.filter(s => s.price_position && s.price_position < 1.0)
    if (expensive.length > 0 && cheaper.length > 0) {
      const best = cheaper.sort((a, b) => a.price_position - b.price_position)[0]
      const worst = expensive.sort((a, b) => b.price_position - a.price_position)[0]
      const savings = worst.spend * (1 - best.price_position / worst.price_position)
      if (savings > 500) {
        opps.push({
          type: 'quick_win',
          title: `${p.pn}${p.description ? ' (' + p.description + ')' : ''} — switch from ${worst.name.substring(0, 25)} to ${best.name.substring(0, 25)}`,
          parts: [p],
          spendExposed: worst.spend,
          estSavings: savings,
          confidence: 'High',
          detail: `${worst.name} charges ${worst.price_position.toFixed(2)}x while ${best.name} is at ${best.price_position.toFixed(2)}x.`,
        })
      }
    }
  })

  opps.sort((a, b) => b.estSavings - a.estSavings)
  return opps
}
